import { LanguageType } from '../constants'

const DEFAULT_BUTTON_COLOR = '#1f6f5b'

type AlexWidgetErrorReason =
  | 'alex_widget_config_missing'
  | 'alex_widget_script_load_failed'
  | 'alex_widget_loader_runtime_error'
  | 'alex_widget_launch_timeout'
  | 'alex_widget_unknown_error'

export class AlexWidgetError extends Error {
  kind: string

  reason: AlexWidgetErrorReason

  constructor({
    message,
    kind,
    reason,
  }: {
    message: string
    kind: string
    reason: AlexWidgetErrorReason
  }) {
    super(message)
    this.name = 'AlexWidgetError'
    this.kind = kind
    this.reason = reason
  }
}

type AlexConnectWidgetConfig = {
  snippetId: string
  widgetUrl: string
  widgetId: string
  buttonColor: string
}

type AmazonConnectCommand = [string, ...unknown[]]

type AmazonConnectFunction = ((...args: AmazonConnectCommand) => void) & {
  ac?: AmazonConnectCommand[]
}

type StartAlexConnectCallParams = {
  event?: Event
  hotelId: string
  language: LanguageType
  ownerToken?: object
  signal?: AbortSignal
  onClose: () => void
  onContactId?: (contactId: string) => void
}

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Window {
    amazon_connect?: AmazonConnectFunction
  }
}

let loadedScriptKey: string | undefined
let loadingPromise: Promise<void> | undefined
let originalCreateElement: typeof Document.prototype.createElement | undefined
let iframePermissionPatchCount = 0
let activeCallPromise: Promise<void> | undefined
let activeCallCleanup:
  | { cleanup: () => void; ownerToken: object | undefined }
  | undefined
let programmaticLaunchCallback: ((event: Event) => void) | undefined
let programmaticLaunchCallbackOwner: AmazonConnectFunction | undefined
let programmaticLaunchCallbackScriptKey: string | undefined

function isMissingEnv(value: string | undefined) {
  return !value || value.includes('REPLACE_WITH')
}

export function getAlexConnectWidgetConfig(): AlexConnectWidgetConfig {
  const {
    VITE_ALEX_CONNECT_SNIPPET_ID: snippetId,
    VITE_ALEX_CONNECT_WIDGET_URL: widgetUrl,
    VITE_ALEX_CONNECT_WIDGET_ID: widgetId,
    VITE_ALEX_CONNECT_BUTTON_COLOR: buttonColor,
  } = import.meta.env

  if (
    isMissingEnv(snippetId) ||
    isMissingEnv(widgetUrl) ||
    isMissingEnv(widgetId)
  ) {
    throw new AlexWidgetError({
      message: 'Alex Connect Widget configuration is missing.',
      kind: 'missingConfig',
      reason: 'alex_widget_config_missing',
    })
  }

  return {
    snippetId: snippetId as string,
    widgetUrl: widgetUrl as string,
    widgetId: widgetId as string,
    buttonColor: buttonColor || DEFAULT_BUTTON_COLOR,
  }
}

export function buildAlexContactAttributes({
  hotelId,
  language,
  clientOrigin,
}: {
  hotelId: string
  language: LanguageType
  clientOrigin: string
}) {
  return {
    hotel_id: hotelId,
    hotelId,
    language_key: language,
    languageKey: language,
    is_staying: '1',
    isStaying: '1',
    support_channel: 'suitebook_calls_web',
    supportChannel: 'suitebook_calls_web',
    client_origin: clientOrigin,
  }
}

export function ensureAmazonConnectQueue() {
  if (window.amazon_connect) return

  window.amazon_connect = (...args: AmazonConnectCommand) => {
    window.amazon_connect!.ac = window.amazon_connect!.ac || []
    window.amazon_connect!.ac.push(args)
  }
}

function appendPermission(iframe: HTMLIFrameElement) {
  const requiredPermissions = [
    'microphone *',
    'camera *',
    'autoplay *',
    'speaker-selection *',
  ]
  const permissions = new Map<string, string>()

  iframe
    .getAttribute('allow')
    ?.split(';')
    .map((permission) => permission.trim())
    .filter(Boolean)
    .forEach((permission) => {
      permissions.set(permission.split(/\s+/)[0], permission)
    })

  requiredPermissions.forEach((permission) => {
    permissions.set(permission.split(/\s+/)[0], permission)
  })

  iframe.setAttribute('allow', [...permissions.values()].join('; '))
}

export function installAlexIframePermissionPatch() {
  iframePermissionPatchCount += 1

  if (!originalCreateElement) {
    originalCreateElement = Document.prototype.createElement
    Document.prototype.createElement =
      function createElementWithIframePermission(
        this: Document,
        tagName: string,
        options?: ElementCreationOptions
      ) {
        const element = originalCreateElement!.call(this, tagName, options)

        if (
          tagName.toLowerCase() === 'iframe' &&
          element instanceof HTMLIFrameElement
        ) {
          appendPermission(element)
        }

        return element
      } as typeof Document.prototype.createElement
  }

  document.querySelectorAll('iframe').forEach((iframe) => {
    appendPermission(iframe)
  })

  return () => {
    iframePermissionPatchCount = Math.max(iframePermissionPatchCount - 1, 0)

    if (iframePermissionPatchCount === 0 && originalCreateElement) {
      Document.prototype.createElement = originalCreateElement
      originalCreateElement = undefined
    }
  }
}

export function loadAlexWidgetScript(config: AlexConnectWidgetConfig) {
  const scriptKey = `${config.widgetUrl}::${config.widgetId}`
  const existingScript = document.getElementById(config.widgetId)

  if (loadedScriptKey === scriptKey && loadingPromise && existingScript) {
    return loadingPromise
  }

  loadedScriptKey = scriptKey
  loadingPromise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.src = config.widgetUrl
    script.async = true
    script.id = config.widgetId
    script.onload = () => {
      resolve()
    }
    script.onerror = () => {
      script.remove()
      if (loadedScriptKey === scriptKey) {
        loadedScriptKey = undefined
        loadingPromise = undefined
      }
      reject(
        new AlexWidgetError({
          message: 'Alex Connect Widget script failed to load.',
          kind: 'scriptLoadFailed',
          reason: 'alex_widget_script_load_failed',
        })
      )
    }
    document.head.appendChild(script)
  })

  return loadingPromise
}

export function preloadAlexWidgetScriptAsset() {
  let config: AlexConnectWidgetConfig
  try {
    config = getAlexConnectWidgetConfig()
  } catch {
    return
  }

  const preloadHref = new URL(config.widgetUrl, window.location.href).href
  const existingPreload = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="preload"]')
  ).some(
    (link) =>
      link.getAttribute('as') === 'script' && link.href === preloadHref
  )

  if (existingPreload) return

  const link = document.createElement('link')
  link.rel = 'preload'
  link.as = 'script'
  // jsdom does not reflect link.as into the attribute, but browsers use the property.
  link.setAttribute('as', 'script')
  link.href = config.widgetUrl
  document.head.appendChild(link)
}

export function installAlexWidgetRuntimeErrorListener(
  onError: (error: AlexWidgetError) => void
) {
  const listener = (event: ErrorEvent) => {
    const message = event.message || ''
    const filename = event.filename || ''
    const isConnectWidgetFile = /amazon-connect-chat-interface-client/.test(
      filename
    )

    if (
      isConnectWidgetFile &&
      (/Failed to load widget/i.test(message) || /widget/i.test(message))
    ) {
      onError(
        new AlexWidgetError({
          message,
          kind: 'loaderRuntimeError',
          reason: 'alex_widget_loader_runtime_error',
        })
      )
    }
  }

  window.addEventListener('error', listener)

  return () => {
    window.removeEventListener('error', listener)
  }
}

function isAlexWidgetIframe(node: Node) {
  if (!(node instanceof HTMLIFrameElement)) return false

  return (
    node.id === 'amazon-connect-chat-widget-iframe' ||
    node.name === 'amazon-connect-chat-widget' ||
    node.title === 'Chat Widget'
  )
}

function createAbortError() {
  return new DOMException('Aborted', 'AbortError')
}

function resetLoadedAlexWidget(config: AlexConnectWidgetConfig) {
  document.getElementById(config.widgetId)?.remove()
  loadedScriptKey = undefined
  loadingPromise = undefined
  programmaticLaunchCallback = undefined
  programmaticLaunchCallbackOwner = undefined
  programmaticLaunchCallbackScriptKey = undefined
  delete window.amazon_connect
}

export function stopActiveAlexConnectCall(ownerToken?: object) {
  if (!activeCallCleanup) return
  if (ownerToken && activeCallCleanup.ownerToken !== ownerToken) return

  activeCallCleanup.cleanup()
}

export function startAlexConnectCall({
  event,
  hotelId,
  language,
  ownerToken,
  signal,
  onClose,
  onContactId,
}: StartAlexConnectCallParams): Promise<void> {
  if (activeCallPromise) {
    return activeCallPromise
  }

  if (signal?.aborted) {
    return Promise.reject(createAbortError())
  }

  let config: AlexConnectWidgetConfig
  try {
    config = getAlexConnectWidgetConfig()
  } catch (error) {
    return Promise.reject(error)
  }
  const scriptKey = `${config.widgetUrl}::${config.widgetId}`

  const currentPromise = new Promise<void>((resolve, reject) => {
    let launchCallback: ((event: Event) => void) | undefined
    let settled = false
    let cleaned = false
    let closed = false
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const uninstallIframePatch = installAlexIframePermissionPatch()
    const disposers: Array<() => void> = [uninstallIframePatch]
    const cleanupActiveCall = () => {
      cleanup({ notifyClose: false })
    }

    const cleanup = ({ notifyClose }: { notifyClose: boolean }) => {
      if (!cleaned) {
        cleaned = true
        document.body.dataset.alexCallActive = 'false'
        disposers.forEach((dispose) => {
          dispose()
        })
        if (timeoutId) clearTimeout(timeoutId)
        if (activeCallPromise === currentPromise) {
          activeCallPromise = undefined
        }
        if (activeCallCleanup?.cleanup === cleanupActiveCall) {
          activeCallCleanup = undefined
        }
        resetLoadedAlexWidget(config)
      }

      if (notifyClose && !closed) {
        closed = true
        onClose()
      }
    }
    activeCallCleanup = { cleanup: cleanupActiveCall, ownerToken }

    const rejectOnce = (error: unknown) => {
      if (settled) return
      settled = true
      cleanup({ notifyClose: false })
      reject(error)
    }

    const resolveOnce = () => {
      if (settled) return
      settled = true
      if (activeCallPromise === currentPromise) {
        activeCallPromise = undefined
      }
      resolve()
    }

    const abortListener = () => {
      if (!settled) {
        rejectOnce(createAbortError())
      }
    }
    signal?.addEventListener('abort', abortListener, { once: true })
    disposers.push(() => {
      signal?.removeEventListener('abort', abortListener)
    })

    ensureAmazonConnectQueue()

    const observer = new MutationObserver((mutations) => {
      const removedWidget = mutations.some((mutation) =>
        Array.from(mutation.removedNodes).some(isAlexWidgetIframe)
      )

      if (removedWidget) {
        cleanup({ notifyClose: true })
      }
    })
    observer.observe(document.body, { childList: true, subtree: true })
    disposers.push(() => {
      observer.disconnect()
    })

    disposers.push(
      installAlexWidgetRuntimeErrorListener((error) => {
        rejectOnce(error)
      })
    )

    const buttonColor = config.buttonColor
    window.amazon_connect?.('styles', {
      iconType: 'VOICE',
      openChat: { color: '#ffffff', backgroundColor: buttonColor },
      closeChat: { color: '#ffffff', backgroundColor: buttonColor },
    })
    window.amazon_connect?.('enableLogs', true)
    window.amazon_connect?.('language', language === 'ja' ? 'ja_JP' : 'en_US')
    window.amazon_connect?.(
      'contactAttributes',
      buildAlexContactAttributes({
        hotelId,
        language,
        clientOrigin: window.location.origin,
      })
    )
    window.amazon_connect?.('webCallingCustomizationObject', {
      fullscreen: { displayButton: 'ENABLED', fullscreenOnLoad: 'ENABLED' },
      devices: { defaultCamera: 'Front' },
      videoTile: {
        localVideoObjectFit: 'cover',
        remoteVideoObjectFit: 'cover',
      },
    })
    window.amazon_connect?.('snippetId', config.snippetId)
    window.amazon_connect?.('contactMetadataHandler', (metadata: unknown) => {
      const contactMetadata = metadata as
        | { contactId?: string; InitialContactId?: string }
        | undefined
      const contactId =
        contactMetadata?.contactId ||
        contactMetadata?.InitialContactId ||
        (typeof metadata === 'string' ? metadata : undefined)

      if (contactId) {
        onContactId?.(contactId)
      }
    })
    window.amazon_connect?.('registerCallback', {
      WIDGET_FRAME_CLOSED: () => {
        cleanup({ notifyClose: true })
      },
      CHAT_ENDED: () => {
        cleanup({ notifyClose: true })
      },
    })
    window.amazon_connect?.('customLaunchBehavior', {
      skipIconButtonAndAutoLaunch: true,
      alwaysHideWidgetButton: true,
      programmaticLaunch: (callback: (event: Event) => void) => {
        launchCallback = callback
        programmaticLaunchCallback = callback
        programmaticLaunchCallbackOwner = window.amazon_connect
        programmaticLaunchCallbackScriptKey = scriptKey
      },
    })

    loadAlexWidgetScript(config)
      .then(() => {
        if (signal?.aborted) {
          rejectOnce(createAbortError())
          return
        }

        timeoutId = setTimeout(() => {
          rejectOnce(
            new AlexWidgetError({
              message: 'Alex Connect Widget launch callback timed out.',
              kind: 'launchTimeout',
              reason: 'alex_widget_launch_timeout',
            })
          )
        }, 15_000)

        const waitForLaunchCallback = () => {
          if (settled) return
          if (signal?.aborted) {
            rejectOnce(createAbortError())
            return
          }
          const cachedLaunch =
            programmaticLaunchCallbackOwner === window.amazon_connect &&
            programmaticLaunchCallbackScriptKey === scriptKey
              ? programmaticLaunchCallback
              : undefined
          const launch = launchCallback || cachedLaunch
          if (launch) {
            if (timeoutId) clearTimeout(timeoutId)
            document.body.dataset.alexCallActive = 'true'
            launch(event ?? new Event('click'))
            resolveOnce()
            return
          }

          window.setTimeout(waitForLaunchCallback, 0)
        }

        waitForLaunchCallback()
      })
      .catch(rejectOnce)
  })

  activeCallPromise = currentPromise
  return currentPromise
}
